import clsx from 'clsx'
import type { PropsWithChildren } from 'react'
import { useCallback } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import TextButton from 'LuxKit/Button/TextButton'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LineCalendarAlertIcon from 'LuxKit/Icons/line/LineCalendarAlertIcon'
import LineCalendarCheckIcon from 'LuxKit/Icons/line/LineCalendarCheckIcon'
import LineCalendarPlusIcon from 'LuxKit/Icons/line/LineCalendarPlusIcon'
import Tooltip from 'LuxKit/Tooltip'
import noop from 'lib/function/noop'
import { preventDefault, stopPropagation } from 'lib/web/eventHandlerUtils'

const ItineraryChip = styled(AssistChip)`
  &:not(.in-itinerary) {
    gap: 0;
  }
`

interface Props {
  label?: string
  onClick?: () => void
  itineraryDate?: moment.Moment
  isProcessing?: boolean
  variant: 'chip' | 'button'
  hasWarning?: boolean
}

/**
 * For bookmarking something and adding to the itinerary at the same time
 */
const BaseBookmarkToItineraryButton = React.forwardRef<
  HTMLButtonElement,
  PropsWithChildren<Props>
>((props, ref) => {
  const {
    onClick = noop,
    itineraryDate,
    isProcessing = false,
    variant,
    hasWarning = false,
  } = props

  const intl = useIntl()
  const removeFromItineraryLabel = intl.formatMessage({
    id: 'xKOmxZiu3QU5BEUMC4Y4',
    defaultMessage: 'Remove from itinerary',
    description: 'Aria label for button to remove an item from the trip itinerary',
  })
  const addToItineraryLabel = intl.formatMessage({
    id: 'yrDIvOcwtI1Y6hP_eUr0',
    defaultMessage: 'Add to itinerary',
    description: 'Aria label for button to add an item to the trip itinerary',
  })
  const moveToAnotherDayLabel = intl.formatMessage({
    id: 'f9pSpgkM8Ys_7PMkxuk5',
    defaultMessage: 'Move to another day',
    description: 'Tooltip and aria label for chip to move an itinerary item to a different day',
  })

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      preventDefault(e)
      stopPropagation(e)

      onClick()
    },
    [onClick],
  )

  let startIcon
  if (hasWarning) {
    startIcon = <LineCalendarAlertIcon colour="warning" />
  } else if (itineraryDate) {
    startIcon = <LineCalendarCheckIcon />
  } else {
    startIcon = <LineCalendarPlusIcon />
  }

  return variant === 'button' ? (
    <TextButton
      ref={ref}
      aria-label={itineraryDate ? removeFromItineraryLabel : addToItineraryLabel}
      kind="tertiary"
      onClick={handleClick}
      disabled={isProcessing}
      size="large"
      startIcon={startIcon}
    >
      {intl.formatMessage({
        id: 'ez5RmLf4QLUVaAumqtAQ',
        defaultMessage: 'Move to',
        description: 'Button label to move a bookmarked item to a different day in the itinerary',
      })}
    </TextButton>
  ) : (
    <Tooltip
      description={itineraryDate ? moveToAnotherDayLabel : addToItineraryLabel}
    >
      <ItineraryChip
        ref={ref}
        aria-label={itineraryDate ? moveToAnotherDayLabel : addToItineraryLabel}
        className={clsx({ 'in-itinerary': !!itineraryDate })}
        variant="filled"
        size="small"
        onClick={handleClick}
        startIcon={startIcon}
        disabled={isProcessing}
      />
    </Tooltip>
  )
})

BaseBookmarkToItineraryButton.displayName = 'BaseBookmarkToItineraryButton'

export default BaseBookmarkToItineraryButton
